<!-- src/pages/Onboarding.vue -->
<template>
  <div class="min-h-screen relative overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white">
    <!-- Ambient glows -->
    <div class="absolute top-0 -left-32 w-[500px] h-[500px] bg-indigo-200/40 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute top-0 right-0 w-[400px] h-[400px] bg-purple-200/25 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-100/30 rounded-full blur-3xl pointer-events-none"></div>

    <!-- Header -->
    <header class="pt-8 pb-6 text-center relative z-10">
      <div class="inline-flex items-center justify-center gap-3 mb-4">
        <img :src="logoUrl" alt="Smart Teacher" class="w-14 h-14 rounded-2xl shadow-lg shadow-indigo-500/15" />
      </div>
      <h1 class="text-4xl font-bold text-gray-900 mb-2">Welcome to Smart Teacher!</h1>
      <p class="text-xl text-gray-500">Let's set up your teaching workspace</p>
    </header>

    <!-- Main content -->
    <main class="max-w-xl mx-auto px-4 pb-12">
      <div class="bg-white rounded-3xl shadow-2xl overflow-hidden">
        <!-- Progress indicator -->
        <div class="px-8 pt-6">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-600">Step {{ displayStep }} of 2</span>
            <span class="text-sm text-gray-400">{{ stepLabel }}</span>
          </div>
          <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-bc-indigo-500 rounded-full transition-all duration-300"
              :style="{ width: `${(displayStep / 2) * 100}%` }"
            ></div>
          </div>
        </div>

        <!-- Step 1: Workspace name -->
        <div v-if="currentStep === 1" class="p-8 space-y-6">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-bc-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <font-awesome-icon
                :icon="['fas', 'house-user']"
                class="text-3xl text-bc-indigo-500"
              />
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Name Your Workspace</h2>
            <p class="text-gray-500">
              Give your teaching workspace a name. This is optional but helps you stay organized.
            </p>
          </div>

          <div>
            <label for="workspaceName" class="block text-sm font-medium text-gray-700 mb-2">
              Workspace Name <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="workspaceName"
              v-model="form.name"
              type="text"
              placeholder="e.g., My Teaching, Ms. Smith's Classes, Private Tutoring"
              class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent text-lg"
            />
            <p class="mt-2 text-sm text-gray-400">You can change this later in settings.</p>
          </div>

          <AppButton
            variant="primary"
            size="md"
            full-width
            @click="nextStep"
          >
            <span>Continue</span>
            <font-awesome-icon :icon="['fas', 'arrow-right']" />
          </AppButton>
        </div>

        <!-- Step 2: School Type (prototype — currently skipped) -->
        <div v-if="false && currentStep === 2" class="p-8 space-y-6">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-bc-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <font-awesome-icon :icon="['fas', 'school']" class="text-3xl text-bc-indigo-500" />
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">What type of school?</h2>
            <p class="text-gray-500">
              We'll set up your teaching years and subjects automatically.
            </p>
          </div>

          <!-- Loading state -->
          <div v-if="loadingSchoolTypes" class="flex justify-center py-8">
            <div
              class="w-8 h-8 border-3 border-bc-indigo-200 border-t-bc-indigo-500 rounded-full animate-spin"
            ></div>
          </div>

          <!-- Error state -->
          <div v-else-if="schoolTypeError" class="text-center py-4">
            <p class="text-gray-500 mb-4">
              Unable to load school types. You can continue and add years and subjects manually.
            </p>
          </div>

          <!-- School type options -->
          <div v-else class="space-y-3">
            <button
              v-for="option in schoolTypeOptions"
              :key="option.type"
              type="button"
              class="w-full p-4 border rounded-xl text-left transition-all"
              :class="
                selectedSchoolType?.type === option.type
                  ? 'border-bc-indigo-500 bg-bc-indigo-50 ring-2 ring-bc-indigo-500'
                  : 'border-gray-200 hover:border-bc-indigo-300'
              "
              @click="selectSchoolType(option)"
            >
              <div class="flex items-center gap-3">
                <div
                  v-if="selectedSchoolType?.type === option.type"
                  class="w-6 h-6 flex items-center justify-center"
                >
                  <font-awesome-icon :icon="['fas', 'check-circle']" class="text-bc-indigo-500" />
                </div>
                <div class="flex-1">
                  <span class="block font-semibold text-gray-900">{{ option.displayName }}</span>
                  <span class="text-sm text-gray-500">{{ option.description }}</span>
                </div>
              </div>
            </button>
          </div>

          <!-- Preview of selected school type -->
          <div v-if="selectedSchoolType" class="bg-gray-50 rounded-xl p-4 space-y-4">
            <p class="text-sm font-medium text-gray-700">What we'll set up for you</p>

            <!-- Teaching Years -->
            <div>
              <div class="flex items-center gap-2 mb-2">
                <font-awesome-icon :icon="['fas', 'users']" class="text-bc-indigo-500" />
                <span class="text-sm font-medium text-gray-700"
                  >Teaching Years ({{ selectedSchoolType?.teachingYears.length }})</span
                >
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="year in selectedSchoolType?.teachingYears.slice(0, 8)"
                  :key="year"
                  class="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-700"
                >
                  {{ year }}
                </span>
                <span
                  v-if="(selectedSchoolType?.teachingYears.length ?? 0) > 8"
                  class="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-500"
                >
                  +{{ (selectedSchoolType?.teachingYears.length ?? 0) - 8 }} more
                </span>
              </div>
            </div>

            <!-- Subjects -->
            <div>
              <div class="flex items-center gap-2 mb-2">
                <font-awesome-icon :icon="['fas', 'book']" class="text-bc-indigo-500" />
                <span class="text-sm font-medium text-gray-700"
                  >Subjects ({{ selectedSchoolType?.subjects.length }})</span
                >
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="subject in selectedSchoolType?.subjects.slice(0, 8)"
                  :key="subject"
                  class="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-700"
                >
                  {{ subject }}
                </span>
                <span
                  v-if="(selectedSchoolType?.subjects.length ?? 0) > 8"
                  class="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-500"
                >
                  +{{ (selectedSchoolType?.subjects.length ?? 0) - 8 }} more
                </span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-2">
            <AppButton
              variant="secondary"
              size="md"
              @click="currentStep = 1"
            >
              Back
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              class="flex-1"
              :disabled="!selectedSchoolType && schoolTypeOptions.length > 0"
              @click="nextStep"
            >
              <span>Continue</span>
              <font-awesome-icon :icon="['fas', 'arrow-right']" />
            </AppButton>
          </div>

          <button
            class="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
            @click="skipSchoolType"
          >
            Skip - I'll set this up manually
          </button>
        </div>

        <!-- Step 3: Academic Year -->
        <div v-if="currentStep === 3" class="p-8 space-y-6">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-bc-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <font-awesome-icon
                :icon="['fas', 'calendar-days']"
                class="text-3xl text-bc-indigo-500"
              />
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Set Up Your Academic Year</h2>
            <p class="text-gray-500">
              All your classes and data will be organized by academic year.
            </p>
          </div>

          <!-- Quick presets -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Quick Setup</label>
            <div class="grid grid-cols-2 gap-3">
              <button
                v-for="preset in yearPresets"
                :key="preset.label"
                type="button"
                class="p-3 border rounded-xl text-left transition-all"
                :class="
                  selectedPreset === preset.label
                    ? 'border-bc-indigo-500 bg-bc-indigo-50 ring-2 ring-bc-indigo-500'
                    : 'border-gray-200 hover:border-bc-indigo-300'
                "
                @click="applyPreset(preset)"
              >
                <span class="block font-semibold text-gray-900">{{ preset.label }}</span>
                <span class="text-sm text-gray-500">{{
                  formatDateRange(preset.startsAt, preset.endsAt)
                }}</span>
              </button>
            </div>
          </div>

          <!-- Custom dates -->
          <div class="pt-4 border-t border-gray-100">
            <label class="block text-sm font-medium text-gray-700 mb-3">Or customize dates</label>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="startDate" class="block text-sm text-gray-500 mb-1">Start Date</label>
                <input
                  id="startDate"
                  v-model="form.academicYear.startsAt"
                  type="date"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500"
                  @change="selectedPreset = null"
                />
              </div>
              <div>
                <label for="endDate" class="block text-sm text-gray-500 mb-1">End Date</label>
                <input
                  id="endDate"
                  v-model="form.academicYear.endsAt"
                  type="date"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500"
                  @change="selectedPreset = null"
                />
              </div>
            </div>
          </div>

          <!-- Year label -->
          <div>
            <label for="yearLabel" class="block text-sm font-medium text-gray-700 mb-2">
              Year Label
            </label>
            <input
              id="yearLabel"
              v-model="form.academicYear.label"
              type="text"
              placeholder="e.g., 2025/2026"
              class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-bc-indigo-500"
            />
          </div>

          <!-- Selected school type summary -->
          <div
            v-if="selectedSchoolType"
            class="bg-bc-indigo-50 border border-bc-indigo-200 rounded-xl p-4"
          >
            <div class="flex items-center gap-3">
              <font-awesome-icon :icon="['fas', 'school']" class="text-bc-indigo-500" />
              <div>
                <p class="text-sm font-medium text-gray-900">
                  {{ selectedSchoolType.displayName }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ selectedSchoolType.teachingYears.length }} teaching years,
                  {{ selectedSchoolType.subjects.length }} subjects will be created
                </p>
              </div>
            </div>
          </div>

          <!-- Error message -->
          <div v-if="error" class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {{ error }}
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-2">
            <AppButton
              variant="secondary"
              size="md"
              @click="currentStep = 1"
            >
              Back
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              class="flex-1"
              :disabled="!isStep3Valid"
              :loading="submitting"
              @click="handleSubmit"
            >
              {{ submitting ? 'Setting up...' : 'Complete Setup' }}
            </AppButton>
          </div>
        </div>
      </div>

      <!-- Help text -->
      <p class="mt-6 text-center text-bc-indigo-200 text-sm">
        Need help? Check out our
        <a href="#" class="text-white underline hover:no-underline">getting started guide</a>
      </p>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { useAuthStore } from '@/store/AuthStore'
import logoUrl from '@/assets/logo.svg'
import { tenantsApi } from '@/api'
import type { SchoolTypeOption } from '@/api'
import { getErrorMessage } from '@/utils/error'
import { useAnalytics } from '@/composables/useAnalytics'

const { capture } = useAnalytics()

const router = useRouter()
const auth = useAuthStore()

const currentStep = ref(1)
const submitting = ref(false)
const error = ref<string | null>(null)
const selectedPreset = ref<string | null>(null)

// Onboarding funnel timing/skip tracking (D11 — onboarding_completed props).
const onboardingStartedAt = ref<number | null>(null)
const skippedSteps = ref<number[]>([])

// School type state
const loadingSchoolTypes = ref(false)
const schoolTypeError = ref(false)
const schoolTypeOptions = ref<SchoolTypeOption[]>([])
const selectedSchoolType = ref<SchoolTypeOption | null>(null)

// Display step (1 or 2 since school type is skipped)
const displayStep = computed(() => (currentStep.value === 3 ? 2 : 1))

// Step label for progress indicator
const stepLabel = computed(() => {
  switch (currentStep.value) {
    case 1:
      return 'Workspace'
    case 3:
      return 'Academic Year'
    default:
      return ''
  }
})

// Form data
const form = ref({
  name: '',
  academicYear: {
    label: '',
    startsAt: '',
    endsAt: '',
  },
})

// Generate year presets based on current date
const yearPresets = computed(() => {
  const now = new Date()
  const currentYear = now.getFullYear()

  return [
    {
      label: `${currentYear}/${currentYear + 1}`,
      startsAt: `${currentYear}-09-01`,
      endsAt: `${currentYear + 1}-07-31`,
    },
    {
      label: `${currentYear - 1}/${currentYear}`,
      startsAt: `${currentYear - 1}-09-01`,
      endsAt: `${currentYear}-07-31`,
    },
    {
      label: `${currentYear}`,
      startsAt: `${currentYear}-01-01`,
      endsAt: `${currentYear}-12-31`,
    },
    {
      label: 'Custom',
      startsAt: '',
      endsAt: '',
    },
  ]
})

const isStep3Valid = computed(() => {
  return (
    form.value.academicYear.label.trim() &&
    form.value.academicYear.startsAt &&
    form.value.academicYear.endsAt &&
    form.value.academicYear.startsAt <= form.value.academicYear.endsAt
  )
})

function formatDateRange(start: string, end: string): string {
  if (!start || !end) return 'Set your own dates'
  const startDate = new Date(start)
  const endDate = new Date(end)
  const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' }
  return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`
}

function applyPreset(preset: (typeof yearPresets.value)[number]) {
  selectedPreset.value = preset.label
  if (preset.label !== 'Custom') {
    form.value.academicYear = {
      label: preset.label,
      startsAt: preset.startsAt,
      endsAt: preset.endsAt,
    }
  }
}

function selectSchoolType(option: SchoolTypeOption) {
  selectedSchoolType.value = option
}

function skipSchoolType() {
  selectedSchoolType.value = null
  currentStep.value = 3
}

function nextStep() {
  if (currentStep.value === 1) {
    const skipped = !form.value.name.trim()
    if (skipped) skippedSteps.value.push(3)
    capture('onboarding_step_completed', {
      step: 'workspace_name_set',
      step_index: 3,
      skipped,
    })
    // Skip school type step (prototype) — go straight to academic year
    currentStep.value = 3
  }
}

async function handleSubmit() {
  if (!isStep3Valid.value) {
    error.value = 'Please fill in all required fields'
    return
  }

  submitting.value = true
  error.value = null

  capture('onboarding_step_completed', {
    step: 'academic_year_set',
    step_index: 4,
    skipped: false,
  })

  try {
    // Call the bootstrap endpoint
    await tenantsApi.bootstrap({
      name: form.value.name.trim() || undefined,
      type: 'INDIVIDUAL',
      schoolType: selectedSchoolType.value?.type,
      firstAcademicYear: {
        label: form.value.academicYear.label.trim(),
        startsAt: form.value.academicYear.startsAt,
        endsAt: form.value.academicYear.endsAt,
      },
    })

    // Refresh the access token to get the new tenant_id claim
    await auth.refreshAccessToken()

    // Refresh user data
    await auth.fetchCurrentUser()

    capture('onboarding_completed', {
      duration_ms: onboardingStartedAt.value ? Date.now() - onboardingStartedAt.value : undefined,
      skipped_steps: [...skippedSteps.value],
    })

    // New users don't need to see the Connect launch announcement
    localStorage.setItem('connect_announcement_seen', 'true')

    // Redirect to the app
    router.push('/home')
  } catch (err: unknown) {
    console.error('Onboarding failed:', err)
    error.value = getErrorMessage(err, 'Failed to complete setup. Please try again.')
  } finally {
    submitting.value = false
  }
}

// On mount, check if user is authenticated
onMounted(async () => {
  if (!auth.isAuthenticated) {
    router.push('/login')
    return
  }

  // Try to fetch user if not loaded
  if (!auth.currentUser) {
    await auth.fetchCurrentUser()
  }

  // If user already has a tenant, redirect to app
  if (auth.hasCompletedOnboarding) {
    router.push('/home')
    return
  }

  // Start the onboarding-duration clock now that we know the user is genuinely onboarding.
  onboardingStartedAt.value = Date.now()

  // Default to first preset
  const firstPreset = yearPresets.value[0]
  if (firstPreset) {
    applyPreset(firstPreset)
  }
})
</script>
